/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import { captureException, captureMessage } from 'embercom/lib/sentry';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import ConversationSummary from 'embercom/objects/inbox/conversation-summary';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import { StateChangeType } from 'embercom/models/data/inbox/state-change-enums';
import type TeammateStateChange from 'embercom/objects/inbox/renderable/state-changes/teammate-state-change';
import { postRequest } from 'embercom/lib/inbox/requests';
import type InboxState from 'embercom/services/inbox-state';
import { InboxEvents } from 'embercom/services/inbox-state';
import type AdminAwayService from 'embercom/services/admin-away-service';
import type AdminPingService from 'embercom/services/admin-ping-service';
import type Nexus from 'embercom/services/nexus';
import { type NexusEvent, NexusEventName, NexusStatusEvents } from 'embercom/services/nexus';
import { task } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

const PROACTIVE_FLUSH_THRESHOLD = 50;
// Since we have minimum inactivity threshold of 10mins on the backend, we use 8mins of inactivity here to flush events
const INACTIVITY_THRESHOLD_MINUTES = 8;
const INACTIVITY_CHECK_INTERVAL_MS = 60 * 1000;

export enum HandlingEventType {
  Resumed = 'resumed',
  Paused = 'paused',
}

export enum PauseReason {
  Switch = 'switch',
  Close = 'close',
  Snooze = 'snooze',
  Reassign = 'reassign',
  AutoAway = 'auto_away',
}

export interface HandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: Date;
  reason?: PauseReason;
}

interface StoredHandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: string;
  reason?: PauseReason;
}

const STORAGE_KEY_PREFIX = 'inbox.handling_events';
const ACTIVE_CONVERSATION_KEY_PREFIX = 'inbox.active_handling_conversation';
const LAST_ACTIVE_KEY_PREFIX = 'inbox.handling_last_active';

export default class ConversationHandlingService extends Service {
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare adminAwayService: AdminAwayService;
  @service declare adminPingService: AdminPingService;
  @service declare nexus: Nexus;

  @tracked activeConversation: Conversation | ConversationSummary | null = null;
  @tracked pendingEvents: HandlingEvent[] = [];

  private flushEventsViaSendBeaconBound!: () => void;
  private handleTabActiveBound!: () => void;
  private handleThreadClosedBound!: (event: NexusEvent) => void;
  private handleNexusReconnectBound!: () => void;
  private handleConversationUpdatedBound!: (
    conversation: Conversation | ConversationSummary,
  ) => void;
  private inactivityCheckTimer: ReturnType<typeof setInterval> | null = null;
  private inactiveMinutes = 0;
  private isInactive = false;
  private lastActiveAt: Date | null = null;

  get storageKey(): string {
    return `${STORAGE_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  get activeConversationStorageKey(): string {
    return `${ACTIVE_CONVERSATION_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  get lastActiveStorageKey(): string {
    return `${LAST_ACTIVE_KEY_PREFIX}-${this.session.workspace.id}-${this.session.teammate.id}`;
  }

  constructor() {
    super(...arguments);
    this.loadFromStorage();
    this.flushEventsViaSendBeaconBound = this.flushEventsViaSendBeacon.bind(this);
    this.handleTabActiveBound = this.handleTabActive.bind(this);
    this.handleThreadClosedBound = this.handleThreadClosed.bind(this);
    this.handleNexusReconnectBound = this.handleNexusReconnect.bind(this);

    window.addEventListener('pagehide', this.flushEventsViaSendBeaconBound);
    document.addEventListener('visibilitychange', this.handleTabActiveBound);

    this.inactivityCheckTimer = setInterval(
      () => this.checkInactivity(),
      INACTIVITY_CHECK_INTERVAL_MS,
    );

    this.nexus.addListener(NexusEventName.ThreadClosed, this.handleThreadClosedBound);
    this.nexus.on(NexusStatusEvents.Disconnected, this.flushEventsViaSendBeaconBound);
    this.nexus.on(NexusStatusEvents.Reconnected, this.handleNexusReconnectBound);

    this.handleConversationUpdatedBound = this.handleConversationUpdated.bind(this);
    this.inboxState.on(InboxEvents.ConversationUpdated, this.handleConversationUpdatedBound);
  }

  willDestroy(): void {
    super.willDestroy();
    if (this.inactivityCheckTimer) {
      clearInterval(this.inactivityCheckTimer);
    }
    window.removeEventListener('pagehide', this.flushEventsViaSendBeaconBound);
    document.removeEventListener('visibilitychange', this.handleTabActiveBound);

    this.nexus.removeListener(NexusEventName.ThreadClosed, this.handleThreadClosedBound);
    this.nexus.off(NexusStatusEvents.Disconnected, this.flushEventsViaSendBeaconBound);
    this.nexus.off(NexusStatusEvents.Reconnected, this.handleNexusReconnectBound);

    this.inboxState.off(InboxEvents.ConversationUpdated, this.handleConversationUpdatedBound);
  }

  private loadFromStorage(): void {
    try {
      let eventsJson = localStorage.getItem(this.storageKey);
      if (eventsJson) {
        let storedEvents = JSON.parse(eventsJson) as StoredHandlingEvent[];
        this.pendingEvents = storedEvents.map((e) => ({
          conversationId: e.conversationId,
          type: e.type,
          timestamp: new Date(e.timestamp),
          reason: e.reason,
        }));
      }

      let lastActiveJson = localStorage.getItem(this.lastActiveStorageKey);
      if (lastActiveJson) {
        this.lastActiveAt = new Date(lastActiveJson);
      }
    } catch (error) {
      captureException(error, {
        tags: { feature: 'conversation_handling_tracking', operation: 'load_from_storage' },
        extra: { storageKey: this.storageKey },
      });
      this.clearEventsStorage();
    }
  }

  private saveToStorage(): void {
    if (this.pendingEvents.length >= PROACTIVE_FLUSH_THRESHOLD) {
      taskFor(this.flushEventsTask).perform();
    }

    try {
      let storedEvents: StoredHandlingEvent[] = this.pendingEvents.map((e) => ({
        conversationId: e.conversationId,
        type: e.type,
        timestamp: e.timestamp.toISOString(),
        reason: e.reason,
      }));
      localStorage.setItem(this.storageKey, JSON.stringify(storedEvents));

      // Save active conversation ID (per-tab via sessionStorage)
      if (this.activeConversation?.id) {
        sessionStorage.setItem(
          this.activeConversationStorageKey,
          String(this.activeConversation.id),
        );
      } else {
        sessionStorage.removeItem(this.activeConversationStorageKey);
      }
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        captureMessage('flush_events_on_quota_exceeded', {
          tags: { feature: 'conversation_handling_tracking', operation: 'save_to_storage' },
          extra: { pendingEventCount: this.pendingEvents.length },
        });
        taskFor(this.flushEventsTask).perform();
      }
    }
  }

  private isQuotaExceededError(error: unknown): boolean {
    return error instanceof DOMException && error.name === 'QuotaExceededError';
  }

  private clearEventsStorage(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // localStorage can throw in restricted environments
    }
    this.pendingEvents = [];
  }

  private shouldTrackConversation(conversation: Conversation | ConversationSummary): boolean {
    let isOpen = conversation.state === ConversationState.Open;
    let isAssignedToMe = conversation.adminAssignee?.id === this.session.teammate.id;
    let isSideConversation =
      conversation instanceof Conversation ? conversation.isSideConversation : false;
    let isValidType =
      !conversation.isBackOfficeTicket && !isSideConversation && !conversation.isTrackerTicket;

    return isOpen && isAssignedToMe && isValidType;
  }

  private alreadyResumed(conversationId: number): boolean {
    let activeMatch = this.activeConversation?.id === conversationId;
    let storedConvId = sessionStorage.getItem(this.activeConversationStorageKey);
    let storageMatch = storedConvId !== null && Number(storedConvId) === conversationId;
    return activeMatch || storageMatch;
  }

  private clearStoredActiveIfMatches(conversationId: number): void {
    let storedConvId = sessionStorage.getItem(this.activeConversationStorageKey);
    if (storedConvId !== null && Number(storedConvId) === conversationId) {
      sessionStorage.removeItem(this.activeConversationStorageKey);
    }
  }

  // Get timestamp from last part if it's an assignment or relevant state change event
  private getLastTrackableEventTimestamp(conversation: Conversation): Date | undefined {
    // Check if conversation has parts before accessing the getter
    if (!conversation.committedParts?.length && !conversation.pendingParts?.length) {
      return undefined;
    }

    let lastPart = conversation.lastPart;
    if (!lastPart) {
      return undefined;
    }

    // Skip optimistic (pending) parts — only use server-committed timestamps
    if (conversation.pendingParts?.includes(lastPart)) {
      return undefined;
    }

    let isTrackableEvent =
      lastPart.renderableType === RenderableType.Assignment ||
      (lastPart.renderableType === RenderableType.StateChange &&
        ((lastPart.renderableData as TeammateStateChange).change === StateChangeType.Opened ||
          (lastPart.renderableData as TeammateStateChange).change === StateChangeType.Unsnoozed));

    return isTrackableEvent ? lastPart.createdAt : undefined;
  }

  private createResumeEvent(
    conversation: Conversation | ConversationSummary,
    timestamp?: Date,
  ): void {
    let lastEventForConversation = this.getLastEventForConversation(conversation.id);
    let isDoubleResume =
      this.alreadyResumed(conversation.id) &&
      lastEventForConversation?.type === HandlingEventType.Resumed;
    if (isDoubleResume) {
      captureMessage('unexpected_double_resume', {
        tags: { feature: 'conversation_handling_tracking', operation: 'resume' },
        extra: {
          conversationId: conversation.id,
          adminId: this.session.teammate.id,
          workspaceId: this.session.workspace.id,
        },
      });
      return;
    }

    let event: HandlingEvent = {
      conversationId: conversation.id,
      type: HandlingEventType.Resumed,
      timestamp: timestamp ?? new Date(),
    };

    this.pendingEvents.push(event);
  }

  private getLastEventForConversation(conversationId: number): HandlingEvent | undefined {
    return [...this.pendingEvents].reverse().find((e) => e.conversationId === conversationId);
  }

  private createPauseEvent(conversationId: number, reason?: PauseReason, timestamp?: Date): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Paused,
      timestamp: timestamp ?? new Date(),
      reason,
    };

    this.pendingEvents.push(event);
  }

  private handleTabActive(): void {
    this.checkShouldResumeAfterInactive();
  }

  private handleThreadClosed(event: NexusEvent): void {
    let conversationId = Number((event.eventData as { conversationId: string }).conversationId);
    if (this.pendingEvents.some((e) => e.conversationId === conversationId)) {
      taskFor(this.flushEventsTask).perform();
    }
  }

  private handleConversationUpdated(conversation: Conversation | ConversationSummary): void {
    let conversationId = conversation?.id;
    if (!conversationId) {
      return;
    }
    let hasEvents = this.pendingEvents.some((e) => e.conversationId === conversationId);
    if (hasEvents && !this.shouldTrackConversation(conversation)) {
      taskFor(this.flushEventsTask).perform();
    }
  }

  private handleNexusReconnect(): void {
    this.checkShouldResumeAfterInactive();
  }

  private checkShouldResumeAfterInactive(): void {
    if (document.hidden) {
      return;
    }

    this.restoreLastActiveAt();

    if (!this.adminAwayService.autoPauseOnAwayEnabled || !this.lastActiveAt) {
      return;
    }

    this.isInactive = false;
    this.inactiveMinutes = 0;
    this.handleReturnFromInactivity();
  }

  private restoreLastActiveAt(): void {
    if (this.lastActiveAt) {
      return;
    }
    try {
      let stored = localStorage.getItem(this.lastActiveStorageKey);
      if (stored) {
        this.lastActiveAt = new Date(stored);
      }
    } catch {
      // localStorage can throw in restricted environments
    }
  }

  private persistLastActiveAt(): void {
    if (this.lastActiveAt) {
      try {
        localStorage.setItem(this.lastActiveStorageKey, this.lastActiveAt.toISOString());
      } catch {
        // localStorage can throw in restricted environments
      }
    }
  }

  private checkInactivity(): void {
    if (!this.adminAwayService.autoPauseOnAwayEnabled) {
      return;
    }

    if (this.adminPingService.userHasActivity) {
      this.inactiveMinutes = 0;

      if (this.isInactive) {
        this.isInactive = false;
        this.handleReturnFromInactivity();
      }
      return;
    }

    this.inactiveMinutes++;

    if (this.inactiveMinutes < INACTIVITY_THRESHOLD_MINUTES || this.isInactive) {
      return;
    }

    if (!this.activeConversation) {
      return;
    }

    this.isInactive = true;
    this.lastActiveAt = new Date();
    this.persistLastActiveAt();
    taskFor(this.flushEventsTask).perform();
    this.activeConversation = null;
  }

  private async handleReturnFromInactivity(): Promise<void> {
    if (!this.lastActiveAt) {
      return;
    }

    let inactiveDurationMs = Date.now() - this.lastActiveAt.getTime();
    if (inactiveDurationMs < INACTIVITY_THRESHOLD_MINUTES * 60 * 1000) {
      return;
    }

    await this.adminAwayService.getAwaySettings();

    let autoChangedTime = this.adminAwayService.autoChangedTime;
    if (!autoChangedTime || new Date(autoChangedTime) <= this.lastActiveAt) {
      this.lastActiveAt = null;
      localStorage.removeItem(this.lastActiveStorageKey);
      return;
    }

    let storedId = sessionStorage.getItem(this.activeConversationStorageKey);
    if (!storedId) {
      this.lastActiveAt = null;
      localStorage.removeItem(this.lastActiveStorageKey);
      return;
    }

    let conversationId = Number(storedId);
    let currentConversation = this.inboxState.activeConversation;

    if (
      !currentConversation ||
      !(
        currentConversation instanceof Conversation ||
        currentConversation instanceof ConversationSummary
      ) ||
      currentConversation.id !== conversationId ||
      !this.shouldTrackConversation(currentConversation)
    ) {
      this.lastActiveAt = null;
      localStorage.removeItem(this.lastActiveStorageKey);
      return;
    }

    let lastEvent = this.pendingEvents[this.pendingEvents.length - 1];
    if (
      lastEvent?.type === HandlingEventType.Resumed &&
      lastEvent.conversationId === conversationId
    ) {
      this.createPauseEvent(conversationId, PauseReason.AutoAway);
    }

    this.createResumeEvent(currentConversation);
    this.activeConversation = currentConversation;
    this.saveToStorage();

    this.lastActiveAt = null;
    localStorage.removeItem(this.lastActiveStorageKey);
  }

  // Called only when switching between conversations
  // Only tracks movements between trackable conversations
  // Non-trackable conversations are ignored - we keep tracking the previous trackable one
  handleConversationSwitch(conversation: Conversation): void {
    let isNewTrackable = this.shouldTrackConversation(conversation);

    // If not trackable, clear storage if it has this conversation (prevents stale storage blocking future resumes)
    if (!isNewTrackable) {
      this.clearStoredActiveIfMatches(conversation.id);
      return;
    }

    // Skip if already tracking this conversation, but hydrate activeConversation if missing (e.g. after page reload)
    if (this.alreadyResumed(conversation.id)) {
      if (!this.activeConversation) {
        this.activeConversation = conversation;
      }
      return;
    }

    // Pause previous conversation if switching to a different one
    // Check both in-memory activeConversation AND sessionStorage (in case of page reload)
    let storedConvId = sessionStorage.getItem(this.activeConversationStorageKey);
    let activeConversationId =
      this.activeConversation?.id ?? (storedConvId && Number(storedConvId));

    if (activeConversationId && activeConversationId !== conversation.id) {
      // If we have the full conversation object in memory, check if it's still trackable
      if (this.activeConversation && this.shouldTrackConversation(this.activeConversation)) {
        this.createPauseEvent(this.activeConversation.id, PauseReason.Switch, new Date());
      } else if (!this.activeConversation) {
        // After page reload, we only have the ID from storage, so pause it
        this.createPauseEvent(activeConversationId, PauseReason.Switch, new Date());
      }
    }

    // Resume the new trackable conversation
    this.createResumeEvent(conversation);
    this.activeConversation = conversation;
    this.saveToStorage();
  }

  // Called when conversation parts change (lastPart updates)
  // If conversation becomes trackable, resume it.
  // If untrackable, clear active so we can resume later.
  handleStateOrAssignmentChange(conversation: Conversation): void {
    let isTrackable = this.shouldTrackConversation(conversation);
    let alreadyResumed = this.alreadyResumed(conversation.id);

    if (isTrackable && !alreadyResumed) {
      let timestamp = this.getLastTrackableEventTimestamp(conversation);

      // Skip if no trackable event timestamp (lastPart is optimistic or not a trackable event type)
      if (!timestamp) {
        return;
      }

      if (this.activeConversation) {
        this.createPauseEvent(this.activeConversation.id, PauseReason.Switch, timestamp);
      }

      this.createResumeEvent(conversation, timestamp);
      this.activeConversation = conversation;
      this.saveToStorage();
    } else if (!isTrackable && alreadyResumed) {
      this.activeConversation = null;
      this.clearStoredActiveIfMatches(conversation.id);
      this.saveToStorage();
    }

    if (this.pendingEvents.length > 0) {
      taskFor(this.flushEventsTask).perform();
    }
  }

  @task({ enqueue: true })
  *flushEventsTask(): TaskGenerator<void> {
    if (this.pendingEvents.length === 0) {
      return;
    }

    try {
      yield this.persistEvents();
      this.clearEventsStorage();
    } catch (error) {
      captureException(error, {
        tags: { feature: 'conversation_handling_tracking', operation: 'flush' },
        extra: {
          pendingEventCount: this.pendingEvents.length,
          conversationIds: [...new Set(this.pendingEvents.map((e) => e.conversationId))],
        },
      });
    }
  }

  private async persistEvents(): Promise<void> {
    let apiEvents = this.pendingEvents.map((e) => ({
      conversation_id: e.conversationId,
      type: e.type,
      timestamp: e.timestamp.toISOString(),
      reason: e.reason,
    }));

    await postRequest(`/ember/inbox/handling_events/batch?app_id=${this.session.workspace.id}`, {
      events: apiEvents,
    });
  }

  private flushEventsViaSendBeacon(): void {
    this.persistLastActiveAt();

    if (this.pendingEvents.length === 0) {
      return;
    }

    let csrfToken =
      document.querySelector("meta[name='csrf-token']")?.getAttribute('content') || '';

    let payload = JSON.stringify({
      authenticity_token: csrfToken,
      events: this.pendingEvents.map((event) => ({
        conversation_id: event.conversationId,
        type: event.type,
        timestamp: event.timestamp.toISOString(),
        reason: event.reason,
      })),
    });

    let url = `/ember/inbox/handling_events/batch?app_id=${this.session.workspace.id}`;

    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'conversation-handling-service': ConversationHandlingService;
  }
}
